import React, { useEffect, useState } from "react";
import { Checkbox, Col, Select, Spin } from "antd";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import EndPoints from "../../../services/end-points";
import FormItem from "../../general/form-item";

interface Props {
  productId?: number;
  selected?: { id: number; name: string } | null;
}

export default function ManufacturingFields({ productId, selected }: Props) {
  const { t, i18n } = useTranslation();
  const { control, setValue } = useFormContext();
  const enabled = useWatch({ control, name: "isManufactured" });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState<{ id: number; name: string }[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const { data } = await EndPoints.product.getAllProducts({
          keyword: search,
          page,
          perPage: 30,
          filters: [
            { name: "isManufactured", operation: "eq", value: false },
            ...(productId
              ? [{ name: "id", operation: "neq", value: productId }]
              : []),
          ],
        });
        if (!active) return;
        setOptions((previous) =>
          page === 1 ? data.data : [...previous, ...data.data],
        );
        setHasMore(data.data.length === 30);
        setFailed(false);
      } catch {
        if (active) {
          setFailed(true);
          setHasMore(false);
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 300);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [enabled, search, page, productId, i18n.language]);

  const choices =
    selected && !options.some((option) => option.id === selected.id)
      ? [selected, ...options]
      : options;
  return (
    <>
      <Col xs={24} md={12}>
        <Controller
          name="isManufactured"
          control={control}
          render={({ field }) => (
            <Checkbox
              checked={Boolean(field.value)}
              onChange={(event) => {
                field.onChange(event.target.checked);
                if (!event.target.checked)
                  setValue("rawMaterialId", null, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
              }}
            >
              {t("manufactured_product")}
            </Checkbox>
          )}
        />
      </Col>
      {enabled ? (
        <Col xs={24} md={12}>
          <FormItem label={t("raw_material")} required>
            <Controller
              name="rawMaterialId"
              control={control}
              rules={{
                validate: (value) =>
                  !enabled ||
                  Boolean(value) ||
                  String(t("manufacturing_select_raw")),
              }}
              render={({ field, fieldState }) => (
                <>
                  <Select
                    {...field}
                    showSearch
                    filterOption={false}
                    loading={loading}
                    aria-label={t("raw_material")}
                    status={fieldState.error ? "error" : undefined}
                    placeholder={t("manufacturing_select_raw")}
                    style={{ width: "100%" }}
                    options={choices.map((option) => ({
                      value: option.id,
                      label: option.name,
                    }))}
                    onSearch={(value) => {
                      setSearch(value);
                      setPage(1);
                      setOptions([]);
                    }}
                    onPopupScroll={(event) => {
                      const target = event.target as HTMLElement;
                      if (
                        !loading &&
                        hasMore &&
                        target.scrollTop + target.clientHeight >=
                          target.scrollHeight - 16
                      )
                        setPage((current) => current + 1);
                    }}
                    notFoundContent={
                      loading ? (
                        <Spin size="small" />
                      ) : (
                        t(failed ? "manufacturing_load_failed" : "no_data")
                      )
                    }
                  />
                  {fieldState.error ? (
                    <div role="alert">{fieldState.error.message}</div>
                  ) : null}
                </>
              )}
            />
          </FormItem>
        </Col>
      ) : null}
    </>
  );
}
